import Modal from '../Modal';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import { LocalizationContext } from 'context/localization.context';
import clsx from 'clsx';
import Icon from '../Icon';
import Button from '../Button';
import { useDispatch } from 'react-redux';
import { AddToCartModel } from 'types/cart-types';
import { AddToCart } from 'store/modules/cart';
import { toast } from 'react-toastify';
import { getAllCadItems } from 'store/api/cartApi';
import Tooltip from '../Tooltip';
import { quickAddValidateCode } from 'api/quickadd';
import { DictionaryModel } from 'types';
import { Int32MaxValue } from 'common/constants';
import { GenericWarning, ServerError } from 'common/helpers';
import { downloadClimateReport } from 'api/export';
interface Props {
	/**api url */
	apiUrl: string;

	api: DictionaryModel;

	/** Pass a toggle modal callback function */
	onToggle: Function;
}

interface CadItemsProps {
	/** Alias code of an article */
	code: string;

	/** Selected quantity of an article */
	quantity?: number;

	/** The name of an article */
	name?: string;

	/** The name of an article */
	position: string;

	/** The name of an article */
	configId: string;

	/** The name of an article */
	configDescription: string;

	/** If the entered article code is valid */
	isValid?: boolean;

	/**optional cad status value that used send in cad to card popup which backend will be used for validation */
	cadStatus?: number;

	/**optional cad article note */
	note?: string;
}

const CadModal = ({ onToggle, apiUrl, api }: Props): JSX.Element => {
	const [cadItems, setCadItems] = useState<CadItemsProps[]>([]);
	const [newRowAdded, setNewRowAdded] = useState<boolean>(false);
	const [isValidating, setIsValidating] = useState<boolean>(false);
	const { t }: any = useContext(LocalizationContext);
	const dispatch = useDispatch();

	const hasValidItems = cadItems.some((item) => item.isValid);

	const columns: any = useMemo(
		() => [
			{ Header: t('cad/position'), accessor: 'position' },
			{ Header: t('cad/itemcode'), accessor: 'code' },
			{ Header: t('cad/quantity'), accessor: 'quantity' }, // even though quantity may exist on the article it's handled separatly to facilitate better UX.
			{ Header: t('cad/itemname'), accessor: 'name' },
			{ Header: t('cad/note'), accessor: 'note' },
		],
		//eslint-disable-next-line
		[]
	);
	const data = useMemo(() => cadItems, [cadItems]);
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data,
	});

	const handleRemoveRow = (index: number) => {
		const newCadItem = cadItems.filter((item, i) => {
			return i !== index;
		});

		setCadItems(newCadItem);
	};

	const handleAddRow = () => {
		setCadItems([
			...cadItems,
			{
				position: '',
				code: '',
				configId: '',
				configDescription: '',
				quantity: 1,
				name: '',
				note: '',
			},
		]);

		setNewRowAdded(true);
	};
	const handleValidation = async (cadItems: CadItemsProps[]) => {
		let validationPayload =
			cadItems &&
			cadItems.map((item: CadItemsProps, index: number) => {
				return {
					code: item.code as string,
					configId: item.configId as String,
					cadStatus: item.cadStatus as Number,
					quantity: item.quantity as number,
				};
			});
		if (validationPayload?.length > 0) {
			setIsValidating(true);
			try {
				const validatedResponse = await quickAddValidateCode(
					api.validateCodeUrl,
					validationPayload,
					true //isCadValidation value - always true from this modal
				);

				/**we loop through the original cadItems and check against validatedresposne arraya( note: both array should be in exact same order and length) */

				const mergedCadItems = cadItems.map((cadItem, index) => {
					const responseItem = validatedResponse[index];

					if (
						responseItem?.code !== cadItem.code &&
						responseItem?.itemCode !== cadItem.code
					) {
						console.warn(
							`Code mismatch at index ${index}:`,
							cadItem.code,
							responseItem?.code
						);
						return cadItem;
					}

					return {
						...cadItem,
						name: responseItem?.name ?? cadItem.name,
						isValid: responseItem?.isValid ?? cadItem.isValid,
					};
				});
				setCadItems(mergedCadItems);
				setIsValidating(false);
			} catch (error) {
				setIsValidating(false);
				console.log(error);
			}
		}
	};
	const handleInputChange = (e: any, cell: any) => {
		let value;
		let newState: CadItemsProps[] = [];

		switch (cell.column.id) {
			case 'quantity':
				value = e.target.value || undefined;
				if (isNaN(value) || value === '' || value < 1) {
					value = undefined;
				} else if (value > Int32MaxValue) {
					value = Int32MaxValue;
				}
				newState = [...cadItems];
				newState[cell.row.id].quantity = value ? parseInt(value) : value;
				setCadItems(newState);
				break;

			case 'code':
				value = e.target.value;
				newState = [...cadItems];
				newState[cell.row.id].code = value;
				setCadItems(newState);
				break;
			case 'position':
				value = e.target.value;
				newState = [...cadItems];
				newState[cell.row.id].position = value;
				setCadItems(newState);
				break;

			default:
				break;
		}
	};

	const handleRowInputBlur = async (cell: any) => {
		if (typeof cell?.value !== 'undefined' && cell?.value !== '') {
			handleValidation(cadItems);
		}
	};
	const handleKeyDown = (e: any) => {
		if (e.key === 'Enter' && !isValidating) {
			handleAddRow();
		}
	};
	//Focus new row when created.
	useEffect(() => {
		if (newRowAdded) {
			const newInput = document.querySelector(
				`#rowinput-${data.length - 1}-code`
			);
			(newInput as HTMLElement)?.focus();
			setNewRowAdded(false);
		}
		//eslint-disable-next-line
	}, [newRowAdded]);

	const handleAddToCart = async () => {
		if (cadItems && cadItems.length > 0) {
			const validCadItems = cadItems.filter((item) => item.isValid === true);
			if (validCadItems.length > 0) {
				let articleList: AddToCartModel[] = [];
				validCadItems.forEach((cadItem: CadItemsProps) => {
					articleList.push({
						itemCode: cadItem.code!,
						quantity: cadItem.quantity!,
						configDescription: cadItem.configDescription,
						configId: cadItem.configId,
					});
				});
				try {
					await dispatch(AddToCart('POST', articleList));
					toast(t('quickadd/transfersuccess'), {
						type: toast.TYPE.SUCCESS,
					});
				} catch (err) {
					if (err instanceof ServerError) {
						toast(t('shared/toast/cartupdateerror'), {
							type: toast.TYPE.ERROR,
						});
					} else if (err instanceof GenericWarning) {
						toast(err.message, { type: toast.TYPE.WARNING });
					}
				} finally {
					setCadItems([]);
					onToggle();
				}
			}
		}
	};
	const exportClimateReport = async (apiUrl: string) => {
		if (!apiUrl) return;
		if (cadItems && cadItems.length > 0) {
			const validCadItems = cadItems.filter((item) => item.isValid === true);
			if (validCadItems.length > 0) {
				let articleList: AddToCartModel[] = [];
				validCadItems.forEach((cadItem: CadItemsProps) => {
					articleList.push({
						itemCode: cadItem.code!,
						quantity: cadItem.quantity!,
					});
				});
				try {
					await downloadClimateReport(apiUrl, articleList);
				} catch (err) {
					toast('Export failed', { type: toast.TYPE.ERROR });
				}
			}
		}
	};
	useEffect(() => {
		const fetchCadItems = async () => {
			try {
				const cadItems = await getAllCadItems(apiUrl);
				setCadItems(cadItems);
				handleValidation(cadItems);
			} catch (error) {
				console.error('Failed to fetch CAD items:', error);
			}
		};

		fetchCadItems();
	}, []);

	return (
		<Modal
			toggleModal={() => onToggle && onToggle()}
			heading={t('quickadd/importfromcadapplication')}
			title={t('quickadd/importfromcadapplication')}
			width="max"
		>
			<div>
				<p className="font-standard text-p mb-12">{t('cad/helptext')}</p>
				<table className="w-full quickimporttable" {...getTableProps()}>
					<caption></caption>
					<thead className="bg-blue">
						{headerGroups.map((headerGroup: any, index: number) => (
							<tr
								key={index}
								className="text-white text-p p-2"
								{...headerGroup.getHeaderGroupProps()}
							>
								<th className="text-sm p-1 max-w-16 md:max-w-25 lg:max-w-none md:text-p md:py-2 md:px-4 font-medium text-center whitespace-no-wrap overflow-hidden overflow-ellipsis">
									{t('quickadd/remove')}
								</th>

								{headerGroup.headers.map((column: any, index: number) => (
									<th
										key={index}
										{...column.getHeaderProps()}
										id={`header-${index}`}
										className="text-sm p-1 text-left max-w-16 md:max-w-25 lg:max-w-none md:text-p md:py-2 md:px-4 font-medium whitespace-no-wrap overflow-hidden overflow-ellipsis"
									>
										{column.render('Header')}
									</th>
								))}

								<th className="text-sm p-1 max-w-16 md:max-w-25 lg:max-w-none md:text-p md:py-2 md:px-4 font-medium text-center whitespace-no-wrap overflow-hidden overflow-ellipsis">
									{t('quickadd/status')}
								</th>
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map((row: any, index: number) => {
							prepareRow(row);
							return (
								<tr
									className={clsx(
										row.original.isValid ? 'row-valid' : 'row-inValid',
										'border-b border-white'
									)}
									key={index}
								>
									<td className="py-2 px-4">
										<button
											className="bg-transparent w-full"
											onClick={() => handleRemoveRow(index)}
										>
											<Icon
												icon="clear"
												aria-label={t('quickadd/removerow')}
												color="#949494"
											/>
										</button>
									</td>
									{row.cells.map((cell: any, index: number) => {
										return (
											<td {...cell.getCellProps()}>
												{cell.column.id === 'code' ||
												cell.column.id === 'position' ? (
													<input
														type="text"
														id={`rowinput-${row.id}-${cell.column.id}`}
														className="text-p py-2 px-4 w-full bg-transparent rounded border-none"
														aria-labelledby={`header-${index}`}
														value={cell.value || ''}
														onChange={(e) => handleInputChange(e, cell)}
														onBlur={() => handleRowInputBlur(cell)}
														onKeyDown={handleKeyDown}
													/>
												) : cell.column.id === 'quantity' ? (
													<input
														type="number"
														id={`rowinput-${row.id}-${cell.column.id}`}
														className="text-p py-2 px-4 w-full bg-transparent rounded border-none"
														aria-labelledby={`header-${index}`}
														value={cell.value || ''}
														onChange={(e) => handleInputChange(e, cell)}
														onKeyDown={handleKeyDown}
													/>
												) : (
													<span className="text-p py-2 px-4">
														{cell.render('Cell')}
													</span>
												)}
											</td>
										);
									})}

									<td className="text-center py-2 px-4">
										{isValidating ? (
											<Icon icon="loader" animate="spin" />
										) : (
											<Tooltip
												title={`${t('quickadd/statusariatitle')} ${t(
													'quickadd/row'
												)} ${row.id}`}
												icon={
													row.original.isValid ? (
														<Icon icon="checkCircle" color="#00B900" />
													) : (
														<Icon icon="alertTriangle" color="#D0021B" />
													)
												}
											>
												{row.original.isValid
													? t('quickadd/validarticletooltip')
													: t('quickadd/invalidarticletooltip')}
											</Tooltip>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				<div className="flex justify-between items-center">
					<div>
						<Button buttonColor="grey" type="button" onClick={handleAddRow}>
							<Icon icon="plus" />
							{t('quickadd/addrow')}
						</Button>
						<Button
							buttonColor="blue"
							type="button"
							className="ml-4"
							onClick={handleAddToCart}
						>
							{t('quickadd/transfertocart')}
						</Button>
					</div>
					{hasValidItems && api?.exportClimateReportApiUrl && (
						<Button
							type="button"
							buttonColor={'blue'}
							className="whitespace-no-wrap"
							onClick={() => exportClimateReport(api.exportClimateReportApiUrl)}
						>
							{t('shared/cartpage/exportclimatereport')}
							<Icon icon="download" size={1.25} aria-hidden="true" />
						</Button>
					)}
				</div>
			</div>
		</Modal>
	);
};
export default CadModal;
